import { CameraControls } from "@react-three/drei";
import CAMERA_CONTROLS from "camera-controls";
import { button, buttonGroup, folder, useControls } from "leva";
import React, { useEffect, useRef } from "react";
import { home, points } from "../../../constants/points";
import { useThree } from "@react-three/fiber";
import { useDevice, useSection } from "../../../common/hooks";
import { SECTIONS } from "../../../constants/sections";
import { setControlLimits } from "../../../utils/controls";

const MAX_MIN_ANGLE = 0.05;
const MAX_MIN_DISTANCE = 2;

export default function CameraControl() {
  const { currentSection } = useSection();
  const { device } = useDevice();
  const meshRef = useRef();
  const cameraControlsRef = useRef();
  const { camera } = useThree();

  useEffect(() => {
    camera.fov = device === "mobile" ? 40 : 30;
    camera.updateProjectionMatrix();
  }, [device]);

  const {
    minDistance,
    maxDistance,
    minMaxAzimuthAngle,
    minMaxPolarAngle,
    enabled,
  } = useControls({
    dollyGrp: buttonGroup({
      label: "dolly",
      opts: {
        1: () => cameraControlsRef.current?.dolly(1, true),
        "-1": () => cameraControlsRef.current?.dolly(-1, true),
      },
    }),
    zoomGrp: buttonGroup({
      label: "zoom",
      opts: {
        "/2": () => cameraControlsRef.current?.zoom(camera.zoom / 2, true),
        "/-2": () => cameraControlsRef.current?.zoom(-camera.zoom / 2, true),
      },
    }),
    moveTo: folder(
      {
        vec1: { value: [3, 5, 2], label: "vec" },
        "moveTo(…vec)": button((get) =>
          cameraControlsRef.current?.moveTo(...get("moveTo.vec1"), true)
        ),
      },
      { collapsed: true }
    ),
    "fitToBox(mesh)": button(() =>
      cameraControlsRef.current?.fitToBox(meshRef.current, true)
    ),
    setPosition: folder(
      {
        vec2: { value: [-5, 2, 1], label: "vec" },
        "setPosition(…vec)": button((get) =>
          cameraControlsRef.current?.setPosition(
            ...get("setPosition.vec2"),
            true
          )
        ),
      },
      { collapsed: true }
    ),
    setTarget: folder(
      {
        vec3: { value: [3, 0, -3], label: "vec" },
        "setTarget(…vec)": button((get) =>
          cameraControlsRef.current?.setTarget(...get("setTarget.vec3"), true)
        ),
      },
      { collapsed: true }
    ),
    setLookAt: folder(
      {
        vec4: { value: [1, 2, 3], label: "position" },
        vec5: { value: [1, 1, 0], label: "target" },
        "setLookAt(…position, …target)": button((get) =>
          cameraControlsRef.current?.setLookAt(
            ...get("setLookAt.vec4"),
            ...get("setLookAt.vec5"),
            true
          )
        ),
      },
      { collapsed: true }
    ),
    lerpLookAt: folder(
      {
        vec6: { value: [-2, 0, 0], label: "posA" },
        vec7: { value: [1, 1, 0], label: "tgtA" },
        vec8: { value: [0, 2, 5], label: "posB" },
        vec9: { value: [-1, 0, 0], label: "tgtB" },
        t: { value: Math.random(), label: "t", min: 0, max: 1 },
        "f(…posA,…tgtA,…posB,…tgtB,t)": button((get) => {
          return cameraControlsRef.current?.lerpLookAt(
            ...get("lerpLookAt.vec6"),
            ...get("lerpLookAt.vec7"),
            ...get("lerpLookAt.vec8"),
            ...get("lerpLookAt.vec9"),
            get("lerpLookAt.t"),
            true
          );
        }),
      },
      { collapsed: true }
    ),
    controlLimits: folder(
      {
        minMaxAzimuthAngle: {
          value: MAX_MIN_ANGLE,
          label: "minMaxAzimuthAngle",
        },
        minMaxPolarAngle: {
          value: MAX_MIN_ANGLE,
          label: "minMaxPolarAngle",
        },
        minDistance: {
          value: MAX_MIN_DISTANCE,
          label: "minMaxDistance",
        },
        maxDistance: {
          value: MAX_MIN_DISTANCE,
          label: "minMaxDistance",
        },
        unlimit: button(() => {
          setControlLimits(cameraControlsRef.current);
        }),
      },
      { collapsed: true }
    ),
    saveState: button(() => cameraControlsRef.current?.saveState()),
    reset: button(() => cameraControlsRef.current?.reset(true)),
    enabled: { value: true, label: "controls on" },
  });

  useEffect(() => {
    const { position, target } =
      currentSection.name === SECTIONS.HOME
        ? home.camera[device]
        : points.find((point) => point.name === currentSection.name).camera[
            device
          ];

    const { current: controls } = cameraControlsRef;
    controls.setLookAt(
      position.x,
      position.y,
      position.z,
      target.x,
      target.y,
      target.z,
      true
    );
    setTimeout(() => {
      setControlLimits(controls, {
        minDistance,
        maxDistance,
        minMaxAzimuthAngle,
        minMaxPolarAngle,
      });
    }, 1100);

    controls.saveState();
  }, [
    currentSection,
    device,
    minDistance,
    maxDistance,
    minMaxAzimuthAngle,
    minMaxPolarAngle,
  ]);

  return (
    <CameraControls
      ref={cameraControlsRef}
      enabled={enabled}
      mouseButtons={{
        right: CAMERA_CONTROLS.ACTION.ROTATE,
        wheel: CAMERA_CONTROLS.ACTION.DOLLY,
      }}
    />
  );
}
